/**
 * Layout composition helpers for ArbiWin UI components
 * Utilities for common layout patterns and compositions
 */

import React from 'react';

/**
 * Layout composition utilities
 */

/**
 * Create a flex layout helper
 * @param {Object} options - Flex options
 * @returns {Object} Flex styles
 */
export const createFlexLayout = function(options = {}) {
  const {
    direction = 'row',
    wrap = 'nowrap',
    justify = 'flex-start',
    align = 'stretch',
    gap = '0',
    inline = false
  } = options;

  return {
    display: inline ? 'inline-flex' : 'flex',
    flexDirection: direction,
    flexWrap: wrap,
    justifyContent: justify,
    alignItems: align,
    gap: gap
  };
};

/**
 * Create a grid layout helper
 * @param {Object} options - Grid options
 * @returns {Object} Grid styles
 */
export const createGridLayout = function(options = {}) {
  const {
    columns = 'auto',
    rows = 'auto',
    gap = '0',
    columnGap,
    rowGap,
    justifyItems = 'stretch',
    alignItems = 'stretch',
    justifyContent = 'start',
    alignContent = 'start',
    inline = false
  } = options;

  const styles = {
    display: inline ? 'inline-grid' : 'grid',
    gridTemplateColumns: columns,
    gridTemplateRows: rows,
    justifyItems,
    alignItems,
    justifyContent,
    alignContent
  };

  if (columnGap !== undefined && rowGap !== undefined) {
    styles.columnGap = columnGap;
    styles.rowGap = rowGap;
  } else {
    styles.gap = gap;
  }

  return styles;
};

/**
 * Common layout patterns
 */
export const layoutPatterns = {
  /**
   * Stack layout (vertical flex)
   */
  stack: function(gap = 'var(--spacing-4)') {
    return createFlexLayout({
      direction: 'column',
      gap: gap
    });
  },

  /**
   * Inline layout (horizontal flex)
   */
  inline: function(gap = 'var(--spacing-4)') {
    return createFlexLayout({
      direction: 'row',
      align: 'center',
      gap: gap
    });
  },

  /**
   * Center layout (flex center)
   */
  center: function() {
    return createFlexLayout({
      justify: 'center',
      align: 'center'
    });
  },

  /**
   * Space between layout
   */
  spaceBetween: function() {
    return createFlexLayout({
      justify: 'space-between',
      align: 'center'
    });
  },

  /**
   * Card grid layout
   */
  cardGrid: function(minWidth = '300px', gap = 'var(--spacing-6)') {
    return createGridLayout({
      columns: `repeat(auto-fit, minmax(${minWidth}, 1fr))`,
      gap: gap
    });
  },

  /**
   * Sidebar layout
   */
  sidebar: function(sidebarWidth = '250px', gap = 'var(--spacing-6)') {
    return createGridLayout({
      columns: `${sidebarWidth} 1fr`,
      gap: gap
    });
  },

  /**
   * Holy grail layout
   */
  holyGrail: function(headerHeight = 'auto', footerHeight = 'auto') {
    return createGridLayout({
      rows: `${headerHeight} 1fr ${footerHeight}`,
      columns: '1fr'
    });
  }
};

/**
 * Layout component factory
 * @param {string} type - Layout type
 * @param {Object} options - Layout options
 * @returns {Function} React component
 */
export const createLayoutComponent = function(type, options = {}) {
  const layoutStyles = layoutPatterns[type] ? layoutPatterns[type](options) : {};

  return function LayoutComponent({ children, style = {}, className = '', ...props }) {
    const combinedStyles = Object.assign({}, layoutStyles, style);

    return React.createElement('div', {
      style: combinedStyles,
      className: `arbiwin-layout-${type} ${className}`.trim(),
      ...props
    }, children);
  };
};

/**
 * Pre-built layout components
 */
export const Stack = createLayoutComponent('stack');
export const Inline = createLayoutComponent('inline');
export const Center = createLayoutComponent('center');
export const SpaceBetween = createLayoutComponent('spaceBetween');
export const CardGrid = createLayoutComponent('cardGrid');
export const Sidebar = createLayoutComponent('sidebar');

/**
 * Layout composition hooks
 */

/**
 * Hook for responsive layout switching
 * @param {Object} layouts - Layout configurations per breakpoint
 * @returns {Object} Current layout styles
 */
export const useResponsiveLayout = function(layouts) {
  const [currentLayout, setCurrentLayout] = React.useState(function() {
    if (typeof window === 'undefined') {
      return layouts.lg || layouts.default || {};
    }

    const width = window.innerWidth;
    if (width >= 1280 && layouts['2xl']) return layouts['2xl'];
    if (width >= 1024 && layouts.xl) return layouts.xl;
    if (width >= 768 && layouts.lg) return layouts.lg;
    if (width >= 640 && layouts.md) return layouts.md;
    if (width >= 0 && layouts.sm) return layouts.sm;
    return layouts.default || {};
  });

  React.useEffect(function() {
    const handleResize = function() {
      const width = window.innerWidth;
      let newLayout;

      if (width >= 1280 && layouts['2xl']) {
        newLayout = layouts['2xl'];
      } else if (width >= 1024 && layouts.xl) {
        newLayout = layouts.xl;
      } else if (width >= 768 && layouts.lg) {
        newLayout = layouts.lg;
      } else if (width >= 640 && layouts.md) {
        newLayout = layouts.md;
      } else if (layouts.sm) {
        newLayout = layouts.sm;
      } else {
        newLayout = layouts.default || {};
      }

      setCurrentLayout(newLayout);
    };

    window.addEventListener('resize', handleResize);
    return function() {
      window.removeEventListener('resize', handleResize);
    };
  }, [layouts]);

  return currentLayout;
};

/**
 * Layout measurement hook
 * @param {React.RefObject} ref - Element ref to measure
 * @returns {Object} Element dimensions
 */
export const useLayoutMeasurement = function(ref) {
  const [dimensions, setDimensions] = React.useState({
    width: 0,
    height: 0,
    top: 0,
    left: 0
  });

  React.useEffect(function() {
    const measureElement = function() {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        setDimensions({
          width: rect.width,
          height: rect.height,
          top: rect.top,
          left: rect.left
        });
      }
    };

    measureElement();

    const resizeObserver = new ResizeObserver(measureElement);
    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return function() {
      resizeObserver.disconnect();
    };
  }, [ref]);

  return dimensions;
};

/**
 * Layout utilities for common calculations
 */
export const layoutUtils = {
  /**
   * Calculate optimal columns for container width
   * @param {number} containerWidth - Container width
   * @param {number} minItemWidth - Minimum item width
   * @param {number} gap - Gap between items
   * @returns {number} Optimal column count
   */
  calculateColumns: function(containerWidth, minItemWidth, gap = 0) {
    if (containerWidth <= 0 || minItemWidth <= 0) return 1;

    const availableWidth = containerWidth - gap;
    const itemWidthWithGap = minItemWidth + gap;
    const columns = Math.floor(availableWidth / itemWidthWithGap);

    return Math.max(1, columns);
  },

  /**
   * Calculate grid item width
   * @param {number} containerWidth - Container width
   * @param {number} columns - Number of columns
   * @param {number} gap - Gap between items
   * @returns {number} Item width
   */
  calculateItemWidth: function(containerWidth, columns, gap = 0) {
    if (columns <= 0) return containerWidth;

    const totalGap = gap * (columns - 1);
    const availableWidth = containerWidth - totalGap;

    return availableWidth / columns;
  },

  /**
   * Generate responsive grid columns string
   * @param {Object} breakpoints - Breakpoint configurations
   * @returns {string} CSS grid-template-columns value
   */
  generateResponsiveColumns: function(breakpoints) {
    const defaultColumns = breakpoints.default || '1fr';

    // This would typically be used with CSS custom properties
    return defaultColumns;
  }
};

/**
 * Layout animation utilities
 */
export const layoutAnimations = {
  /**
   * Fade in animation
   */
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.3 }
  },

  /**
   * Slide up animation
   */
  slideUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3 }
  },

  /**
   * Scale in animation
   */
  scaleIn: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.2 }
  },

  /**
   * Stagger children animation
   */
  staggerChildren: {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  }
};

/**
 * Layout accessibility helpers
 */
export const layoutA11y = {
  /**
   * Generate skip navigation links
   * @param {Array} sections - Array of section objects {id, label}
   * @returns {React.Element} Skip navigation component
   */
  createSkipNavigation: function(sections) {
    return React.createElement('nav', {
      className: 'arbiwin-skip-navigation',
      'aria-label': 'Skip navigation'
    }, sections.map(function(section) {
      return React.createElement('a', {
        key: section.id,
        href: '#' + section.id,
        className: 'arbiwin-skip-link'
      }, 'Skip to ' + section.label);
    }));
  }
};

/**
 * Focus management hook for layout changes
 * @param {React.RefObject} ref - Element to focus
 */
export const useFocusManagement = function(ref) {
  React.useEffect(function() {
    if (ref.current) {
      ref.current.focus();
    }
  }, [ref]);
};

/**
 * Layout debugging utilities (development only)
 */
export const layoutDebug = {
  /**
   * Add visual grid overlay
   * @param {boolean} enabled - Whether debugging is enabled
   * @returns {Object} Debug styles
   */
  gridOverlay: function(enabled = false) {
    if (!enabled || process.env.NODE_ENV === 'production') {
      return {};
    }

    return {
      backgroundImage: `
        linear-gradient(rgba(255, 0, 0, 0.1) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 0, 0, 0.1) 1px, transparent 1px)
      `,
      backgroundSize: '20px 20px'
    };
  },

  /**
   * Add element boundaries
   * @param {boolean} enabled - Whether debugging is enabled
   * @returns {Object} Debug styles
   */
  showBoundaries: function(enabled = false) {
    if (!enabled || process.env.NODE_ENV === 'production') {
      return {};
    }

    return {
      outline: '1px solid red',
      backgroundColor: 'rgba(255, 0, 0, 0.05)'
    };
  }
};

// Export all utilities as default object
const layoutHelpers = {
  createFlexLayout,
  createGridLayout,
  layoutPatterns,
  createLayoutComponent,
  Stack,
  Inline,
  Center,
  SpaceBetween,
  CardGrid,
  Sidebar,
  useResponsiveLayout,
  useLayoutMeasurement,
  useFocusManagement,
  layoutUtils,
  layoutAnimations,
  layoutA11y,
  layoutDebug
};

export default layoutHelpers;
